import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";

const API_BASE = "http://localhost:5002/api";
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// ════════════════════════════════════════════════════════
//  EXCEL EXPORT HELPERS
// ════════════════════════════════════════════════════════
function exportToExcel(rows, fileName = "report", sheetName = "Sheet1") {
  if (!rows || !rows.length) return;
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const colWidths = Object.keys(rows[0]).map((key) => ({
    wch: Math.max(key.length, ...rows.map((r) => String(r[key] ?? "").length)) + 2,
  }));
  worksheet["!cols"] = colWidths;
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

const CATEGORIES = ["All", "Client", "Invoice", "Expense", "Tax", "Financial"];

const styles = {
  wrap: { fontFamily: "'DM Sans', sans-serif" },
  cardsRow: { display: "flex", gap: "14px", marginBottom: "20px", flexWrap: "wrap" },
  card: {
    background: "#fff", border: "1px solid #f0f0f0", borderRadius: "12px",
    padding: "14px 18px", minWidth: "150px", flex: "1",
  },
  cardLabel: { fontSize: "11px", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" },
  cardValue: { fontSize: "20px", fontWeight: 700, color: "#1a1a2e", marginTop: "4px", fontFamily: "'Playfair Display', serif" },
  filterRow: { display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" },
  chip: (active) => ({
    padding: "6px 14px", borderRadius: "20px", border: `1px solid ${active ? "#4f8ef7" : "#e5e5e5"}`,
    background: active ? "#4f8ef7" : "#fff", color: active ? "#fff" : "#555",
    fontSize: "12.5px", fontWeight: 600, cursor: "pointer",
  }),
  actionRow: { display: "flex", gap: "10px", marginBottom: "18px" },
  btnPrimary: {
    padding: "9px 18px", borderRadius: "9px", border: "none", background: "#52c97e",
    color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer",
  },
  btnSecondary: {
    padding: "9px 18px", borderRadius: "9px", border: "1px solid #52c97e", background: "#fff",
    color: "#52c97e", fontSize: "13px", fontWeight: 600, cursor: "pointer",
  },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: "12px", overflow: "hidden", border: "1px solid #f0f0f0" },
  th: { textAlign: "left", fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: ".4px", padding: "12px 16px", borderBottom: "1px solid #f0f0f0" },
  td: { padding: "12px 16px", fontSize: "13px", color: "#1a1a2e", borderBottom: "1px solid #f7f7f7" },
  badge: { fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "20px", background: "#eff6ff", color: "#4f8ef7" },
  downloadBtn: { fontSize: "12px", fontWeight: 600, color: "#4f8ef7", background: "none", border: "1px solid #eff6ff", borderRadius: "7px", padding: "5px 10px", cursor: "pointer" },
  sectionTitle: { fontFamily: "'Playfair Display', serif", fontSize: "16px", fontWeight: 700, color: "#1a1a2e", margin: "26px 0 12px" },
  loading: { textAlign: "center", padding: "40px", color: "#888" },
};

const fmtCAD = (v) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-CA") : "-";

export default function Reporting() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const H = getAuthHeader();
      const [invRes, expRes, clientRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/invoices`, H).then(r => r.json()),
        fetch(`${API_BASE}/expenses`, H).then(r => r.json()),
        fetch(`${API_BASE}/clients`, H).then(r => r.json()),
        fetch(`${API_BASE}/invoices/stats/summary`, H).then(r => r.json()),
      ]);
      setInvoices(invRes.invoices || []);
      setExpenses(expRes.expenses || []);
      setClients(clientRes.clients || []);
      setStats(statsRes.stats || statsRes);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Report rows builder ──
  const buildReports = () => {
    const reports = [];

    // Client Reports
    clients.forEach(c => {
      reports.push({
        id: `client-${c.id}`,
        name: c.client_name,
        category: "Client",
        email: c.email || "-",
        mobile: c.mobile || "-",
        province: c.state || "-",
        status: c.status,
        since: fmtDate(c.client_since),
        total_invoices: c.total_invoices || 0,
        total_revenue: fmtCAD(c.total_revenue),
        outstanding: fmtCAD(c.outstanding_amount),
      });
    });

    // Invoice Reports
    invoices.forEach(inv => {
      reports.push({
        id: `invoice-${inv.id}`,
        name: inv.invoice_number,
        category: "Invoice",
        client: inv.buyer_business_name || "-",
        date: fmtDate(inv.invoice_date),
        due_date: fmtDate(inv.due_date),
        status: inv.status,
        subtotal: fmtCAD(inv.subtotal),
        gst: fmtCAD(inv.gst_amount),
        hst: fmtCAD(inv.hst_amount),
        total_tax: fmtCAD(inv.total_tax),
        total: fmtCAD(inv.total_amount),
        paid: fmtCAD(inv.amount_paid),
        balance: fmtCAD(inv.balance_due),
      });
    });

    // Expense Reports
    expenses.forEach(exp => {
      reports.push({
        id: `expense-${exp.id}`,
        name: exp.title,
        category: "Expense",
        vendor: exp.vendor || "-",
        expense_category: exp.category || "-",
        date: fmtDate(exp.expense_date),
        amount: fmtCAD(exp.amount),
        tax_rate: `${exp.gst || 0}%`,
        tax_amount: fmtCAD(exp.tax_amount),
        total: fmtCAD(exp.total_amount),
        status: exp.status,
        payment_method: exp.payment_method || "-",
      });
    });

    // Tax Reports
    const paidInvoices = invoices.filter(i => i.status === "Paid");
    if (paidInvoices.length) {
      const totalGST = paidInvoices.reduce((s, i) => s + parseFloat(i.gst_amount || 0), 0);
      const totalHST = paidInvoices.reduce((s, i) => s + parseFloat(i.hst_amount || 0), 0);
      const totalPST = paidInvoices.reduce((s, i) => s + parseFloat(i.pst_amount || 0), 0);
      const totalTax = paidInvoices.reduce((s, i) => s + parseFloat(i.total_tax || 0), 0);
      const totalITC = expenses.reduce((s, e) => s + parseFloat(e.tax_amount || 0), 0);
      reports.push({
        id: "tax-summary",
        name: "GST/HST Tax Summary",
        category: "Tax",
        total_gst_collected: fmtCAD(totalGST),
        total_hst_collected: fmtCAD(totalHST),
        total_pst_collected: fmtCAD(totalPST),
        total_tax_collected: fmtCAD(totalTax),
        total_itc_claimable: fmtCAD(totalITC),
        net_remittable: fmtCAD(totalTax - totalITC),
      });
    }

    // Financial Reports
    if (stats) {
      const totalRevenue = parseFloat(stats.total_paid || stats.total_revenue || 0);
      const totalExpense = parseFloat(stats.total_expenses || 0);
      reports.push({
        id: "financial-pl",
        name: "Profit & Loss Summary",
        category: "Financial",
        total_invoices: stats.total || 0,
        paid_invoices: stats.paid || 0,
        total_revenue: fmtCAD(totalRevenue),
        total_expenses: fmtCAD(totalExpense),
        net_profit: fmtCAD(totalRevenue - totalExpense),
        outstanding: fmtCAD(stats.total_outstanding || stats.pending_amount || 0),
      });
    }

    return reports;
  };

  const allReports = buildReports();
  const filteredReports = activeCategory === "All"
    ? allReports
    : allReports.filter(r => r.category === activeCategory);

  const downloadReport = (report) => {
    const { id, ...data } = report;
    exportToExcel([data], report.name.replace(/\s+/g, "_").toLowerCase(), report.category);
  };

  const downloadAllReports = () => {
    const rows = allReports.map(({ id, ...r }) => r);
    exportToExcel(rows, "all-reports", "Reports");
  };

  const downloadCategoryReports = () => {
    const rows = filteredReports.map(({ id, ...r }) => r);
    exportToExcel(rows, `${activeCategory.toLowerCase()}-reports`, activeCategory);
  };

  if (loading) return <div style={styles.loading}>Loading reports...</div>;

  const totalRevenue = parseFloat(stats?.total_paid || stats?.total_revenue || 0);
  const totalExpenses = parseFloat(stats?.total_expenses || 0);

  return (
    <div style={styles.wrap}>
      {/* Summary Cards */}
      <div style={styles.cardsRow}>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Invoices</div>
          <div style={styles.cardValue}>{stats?.total || 0}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Revenue</div>
          <div style={styles.cardValue}>{fmtCAD(totalRevenue)}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Expenses</div>
          <div style={styles.cardValue}>{fmtCAD(totalExpenses)}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Net Profit</div>
          <div style={{ ...styles.cardValue, color: totalRevenue - totalExpenses >= 0 ? "#52c97e" : "#f97b6b" }}>
            {fmtCAD(totalRevenue - totalExpenses)}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div style={styles.filterRow}>
        {CATEGORIES.map((cat) => (
          <button key={cat} style={styles.chip(activeCategory === cat)} onClick={() => setActiveCategory(cat)}>
            {cat}
          </button>
        ))}
      </div>

      {/* Action Buttons */}
      <div style={styles.actionRow}>
        <button style={styles.btnPrimary} onClick={downloadAllReports}>⬇ Download All (Excel)</button>
        <button style={styles.btnSecondary} onClick={downloadCategoryReports}>⬇ Download {activeCategory} (Excel)</button>
      </div>

      {/* Reports Table */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Details</th>
            <th style={styles.th}>Download</th>
          </tr>
        </thead>
        <tbody>
          {filteredReports.length === 0 ? (
            <tr><td style={{ ...styles.td, textAlign: "center", color: "#888" }} colSpan={4}>No data available</td></tr>
          ) : (
            filteredReports.map((r) => (
              <tr key={r.id}>
                <td style={styles.td}>{r.name}</td>
                <td style={styles.td}><span style={styles.badge}>{r.category}</span></td>
                <td style={styles.td} >
                  {r.category === "Client" && `${r.status} · ${r.province} · ${r.total_revenue}`}
                  {r.category === "Invoice" && `${r.status} · ${r.total} · Balance: ${r.balance}`}
                  {r.category === "Expense" && `${r.vendor} · ${r.total} · ${r.status}`}
                  {r.category === "Tax" && `HST: ${r.total_hst_collected} · ITC: ${r.total_itc_claimable} · Net: ${r.net_remittable}`}
                  {r.category === "Financial" && `Revenue: ${r.total_revenue} · Expenses: ${r.total_expenses} · Net: ${r.net_profit}`}
                </td>
                <td style={styles.td}>
                  <button style={styles.downloadBtn} onClick={() => downloadReport(r)}>⬇ XLSX</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}